import { useDomDirection } from '@design-system/hooks';
import { isBrowser } from '@phx-husky/browser-helpers';
import classNames from 'classnames';
import objectHash from 'object-hash';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import SwiperCore, { Autoplay, Pagination, Scrollbar, Thumbs } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useCurrentVariantStateContext } from '~/context/current-variant-state/current-variant-state-context';

import { CarouselControllers } from './components/carousel-controllers';
import { CarouselItem } from './components/carousel-item';
import ThumbsSwiper from './components/carousel-thumbs-swiper';
import { CarouselProps } from './models';
import { generateConfig } from './utils/generateConfig';
import { getSlidesPerViewCount } from './utils/getSlidesPerViewCount';

SwiperCore.use([Thumbs, Pagination, Autoplay]);

export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  (props, ref) => {
    const {
      className,
      items,
      progressIndicator = 'none',
      variant = 'fixed',
      gap = 32,
      wrapperTag = 'ul',
      tag = 'li',
      buttonNext,
      buttonPrev,
      preferredCardSize = 300,
      ariaLivePlaceholder,
      centerInsufficientSlides = true,
      autoplay,
    } = props;

    const scrollClassName = `scroll-${objectHash({
      ...props,
      items: props.items.map(({ caption }) => caption),
    })}`;

    const [params, setParams] = useState({
      progress: 0,
      activeSlide: 0,
      slidesPerView: 0,
      isBeginning: true,
      isEnd: false,
    });

    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
    const swiperRef = useRef<SwiperCore | null>(null);
    const isFirstRender = useRef(true);

    const domDirection = useDomDirection();
    useEffect(() => {
      swiperRef.current?.changeLanguageDirection(domDirection);
    }, [domDirection]);

    const { currentVariant } = useCurrentVariantStateContext();
    useEffect(() => {
      const currentIndex = currentVariant?.imageIndex ?? '0';
      const isIndexValid = parseInt(currentIndex, 10) < items.length;

      if (currentIndex && isIndexValid) {
        setParams({
          ...params,
          activeSlide: Number(currentIndex),
        });

        if (isFirstRender.current) {
          swiperRef.current?.slideTo(Number(currentIndex), 0);
        }
        isFirstRender.current = false;
        swiperRef.current?.slideTo(Number(currentIndex));
      }
      
    }, [currentVariant.imageIndex]);

    const handleProgress = (swiper) => {
      if (variant === 'fixed' && !params.slidesPerView) {
        const count = getSlidesPerViewCount(
          swiper.width,
          gap,
          preferredCardSize
        );
        setParams((prevVal) => ({
          ...prevVal,
          slidesPerView: count,
        }));
      }
      setParams((prevVal) => ({
        ...prevVal,
        progress: Math.abs(swiper.progress),
        isBeginning: swiper.isBeginning,
        isEnd: swiper.progress >= 1,
      }));
    };

    const handleResize = (swiper) => {
      if (variant === 'fixed') {
        const count = getSlidesPerViewCount(
          swiper.width,
          gap,
          preferredCardSize
        );
        setParams((prevVal) => ({
          ...prevVal,
          slidesPerView: count,
        }));
      }
    };

    const handleNextSlide = useCallback(() => {
      swiperRef.current?.slideNext();
    }, []);

    const handlePrevSlide = useCallback(() => {
      swiperRef.current?.slidePrev();
    }, []);

    return (
      <div
        ref={ref}
        className={classNames('cds-carousel', className, {
          'cds-carousel__centered-items': centerInsufficientSlides,
        })}
      >
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          thumbs={{ swiper: thumbsSwiper }}
          wrapperTag={wrapperTag}
          slidesPerView="auto"
          watchSlidesProgress
          spaceBetween={gap}
          shortSwipes={false}
          modules={[Scrollbar, Pagination, Autoplay]}
          scrollbar={
            progressIndicator === 'rails'
              ? { el: `.${scrollClassName || 'swiper-scrollbar'}` }
              : false
          }
          pagination={
            progressIndicator === 'dots'
              ? {
                  el: '.cds-carousel__dots-wrapper .swiper-pagination',
                  clickable: true,
                }
              : false
          }
          autoplay={
            autoplay
              ? {
                  delay: 4500,
                  disableOnInteraction: false,
                }
              : false
          }
          loop={progressIndicator === 'dots'}
          speed={740}
          effect="slide"
          {...generateConfig({
            variant,
            gap,
            preferredCardSize,
          })}
          onProgress={handleProgress}
          onSlideChange={(swiper) => {
            setParams((prevVal) => ({
              ...prevVal,
              activeSlide: swiper.activeIndex,
              isBeginning: swiper.isBeginning,
              isEnd: swiper.progress >= 1,
            }));

            if (swiper.isEnd) {
              thumbsSwiper?.slideTo(items.length - 1);
            }

            if (swiper.isBeginning) {
              thumbsSwiper?.slideTo(0);
            }
          }}
          onBreakpoint={(_, breakpointParams) => {
            setParams((prevVal) => ({
              ...prevVal,
              slidesPerView: Number(breakpointParams.slidesPerView),
            }));
          }}
          onResize={handleResize} 
          
        >
          {items.map(({ children }, index) => (
            <SwiperSlide
              style={{
                width: variant === 'fixed' ? preferredCardSize : '100%',
              }}
              tag={tag}
              key={`swiper-slide${index.toString()}`}
              className={`cds-carousel-slide--${variant}`}
            >
              <CarouselItem
                index={index}
                variant={variant}
                slidesPerView={params.slidesPerView}
              >
                {children}
              </CarouselItem>
            </SwiperSlide>
          ))}
          <div aria-live="polite" className="cds-carousel__sr-only">
            {ariaLivePlaceholder(
              params.activeSlide + 1,
              items.length,
              params.slidesPerView,
              params.progress
            )}
          </div>
        </Swiper>
        <div className="controlls-wrapper">
          {progressIndicator === 'dots' && (
            <div className="cds-carousel__dots-wrapper">
              <div className="swiper-pagination" />
            </div>
          )}
          {items && items.length > 1 && isBrowser() ? (
            <>
              <CarouselControllers
                progressIndicator={progressIndicator}
                isHideControls={swiperRef.current?.isLocked || false}
                totalItems={items.length}
                activeSlide={params.activeSlide}
                caption={items[params.activeSlide]?.caption}
                buttonNext={buttonNext}
                buttonPrev={buttonPrev}
                scrollClassName={scrollClassName}
                isAtBeginning={params.isBeginning}
                isAtEnd={params.isEnd}
                onNextSlide={handleNextSlide}
                onPrevSlide={handlePrevSlide}
              />
              {progressIndicator === 'thumbs' && variant === 'single' && (
                <ThumbsSwiper
                  items={items}
                  tag={tag}
                  gap={gap}
                  setThumbsSwiper={setThumbsSwiper}
                  centerInsufficientSlides={centerInsufficientSlides}
                />
              )}
            </>
          ) : null}
        </div>
        <noscript>
          <style>
            {`
            .swiper-wrapper {
              gap: ${gap}px;
            }

            .cds-carousel-slide--fixed,
            .cds-carousel-slide--fluid {
              width: ${preferredCardSize}px !important;
            }

            .swiper {
              overflow: auto;
            }
            `}
          </style>
        </noscript>
      </div>
    );
  }
);
